<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Groups</h1>
            <a class="btn pull-right" @click="showEditGroups = true">Edit groups</a>
            <a
                class="btn pull-right mr-5"
                @click="
                    newGroupName = '';
                    showAddGroup = true;
                "
                >Add a new group</a
            >
            <p class="lead">
                Create and manage groups for your class. Filter to view books assigned to all members of the group.
            </p>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <v-alert v-if="pupilsNotInClasses.length > 0" type="warning">
                <div>
                    <p class="mb-3">Your Giglets admin manages the classes and pupils who are allocated to you.</p>
                    <p>
                        {{ pupilsNotInClasses.length }} pupil{{ pupilsNotInClasses.length == 1 ? '' : 's' }}
                        {{ pupilsNotInClasses.length == 1 ? 'is' : 'are' }} no longer in your class(es) but remain in
                        one or more of your groups. You can remove them from the groups by selecting 'Clean up groups'.
                    </p>
                </div>
                <div class="d-flex justify-end">
                    <v-btn variant="outlined" @click="cleanUpGroups">Clean up groups</v-btn>
                </div>
            </v-alert>
        </div>
    </div>
    <div v-if="groups.length > 0" class="grid">
        <div class="unit whole">
            <div class="panel">
                <div class="d-flex justify-end mr-5">
                    <div class="w-25">
                        <v-select
                            v-model="filterGroup"
                            label="Filter by group"
                            :items="groups"
                            item-title="name"
                            item-value="uid"
                            clearable
                            density="compact"
                            hide-details
                        >
                        </v-select>
                    </div>
                </div>
                <v-data-table
                    v-model:sort-by="sortBy"
                    density="compact"
                    items-per-page="-1"
                    :headers="headers"
                    :items="items"
                    item-value="id"
                    class="elevation-0"
                >
                    <template #bottom></template>
                    <template #[`item.name`]="{ item }">
                        <span v-if="item.noAccess"
                            >{{ item.name }}
                            <v-icon icon="mdi-information" color="info" />
                            <v-tooltip activator="parent" location="top"
                                >This pupil is no longer in any of your classes</v-tooltip
                            >
                        </span>
                        <RouterLink v-else :to="`/teacher/pupil/${item.id}`">{{ item.name }}</RouterLink>
                    </template>
                    <template #[`item.classes`]="{ value: classes }">
                        {{ classes.map((c: Class) => c.name).join(', ') }}</template
                    >
                    <template #[`item.groups`]="{ item }">
                        <v-select
                            v-model="item.groups"
                            class="my-2"
                            :items="groups"
                            item-title="name"
                            item-value="uid"
                            chips
                            color="blue-grey-lighten-2"
                            label="Select groups"
                            multiple
                            hide-details
                            @update:model-value="updateGroups(item, item.groups as unknown as string[])"
                        >
                            <template #chip="{ item: g }">
                                <v-chip
                                    :text="g.raw.name"
                                    :color="colorNames[hash(g.raw.uid) % colorNames.length]"
                                ></v-chip>
                            </template>
                        </v-select>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <div v-if="sharedBooksInGroup.length" class="grid">
        <div class="unit whole">
            <div class="panel pb-6">
                <h2>Group reading record</h2>
                <p>The following texts have been assigned to all pupils in this group.</p>
                <v-row class="mt-3">
                    <v-col
                        v-for="book in sharedBooksInGroup"
                        :key="book.id"
                        class="d-flex flex-column justify-space-between"
                        cols="2"
                    >
                        <RouterLink :to="`/teacher/book/${book.bookId}/about`">
                            <v-img :src="getImageUrl(book.coverUrl, book)" />
                            <h5 class="mt-2">{{ book.title }}</h5>
                        </RouterLink>
                        <div v-if="book.assigned" class="text-caption">Assigned: {{ formatDate(book.assigned) }}</div>
                    </v-col>
                </v-row>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.pupils || !isLoaded.groups" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-dialog v-model="showEditGroups" :close-on-back="false" width="600">
        <v-card>
            <v-card-title><h2>Edit Groups</h2></v-card-title>
            <v-card-text>
                <v-list lines="two">
                    <v-list-item v-for="group in groups" :key="group.uid" style="min-height: 72px">
                        <template #default>
                            <div v-if="editingNameForGroup !== group.uid">
                                <v-list-item-title>{{ group.name }}</v-list-item-title>
                                <v-list-item-subtitle
                                    >{{ group.pupils.length }} pupil{{
                                        group.pupils.length == 1 ? '' : 's'
                                    }}</v-list-item-subtitle
                                >
                            </div>
                            <div v-else class="d-flex align-center">
                                <v-text-field
                                    v-model="newGroupName"
                                    label="Name"
                                    density="compact"
                                    hide-details
                                ></v-text-field>
                                <a class="btn btn-small ml-5" @click="editingNameForGroup = undefined">Cancel</a>
                                <a class="btn btn-small primary ml-5" @click="saveGroupName(group)">Save</a>
                            </div>
                        </template>
                        <template #prepend>
                            <v-avatar color="grey-lighten-1">
                                <v-icon color="white">mdi-account-group</v-icon>
                            </v-avatar>
                        </template>

                        <template #append>
                            <div v-if="editingNameForGroup == undefined" class="d-flex align-center">
                                <a
                                    class="btn btn-small"
                                    @click="
                                        newGroupName = group.name;
                                        editingNameForGroup = group.uid;
                                    "
                                    >Rename</a
                                >
                                <v-btn
                                    color="grey-lighten-1"
                                    icon="mdi-delete"
                                    variant="text"
                                    @click="deleteGroup(group)"
                                ></v-btn>
                            </div>
                        </template>
                    </v-list-item>
                </v-list>
            </v-card-text>
            <v-card-actions class="mx-5 mb-4">
                <v-spacer />
                <a
                    class="btn primary"
                    :class="{ disabled: editingNameForGroup != undefined }"
                    @click="showEditGroups = false"
                    >Done</a
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog v-model="showAddGroup" :close-on-back="false" width="600">
        <v-card>
            <v-card-title><h2>Add Group</h2></v-card-title>
            <v-card-text>
                <v-text-field v-model="newGroupName" label="Name"></v-text-field>
            </v-card-text>
            <v-card-actions class="mx-5 mb-4">
                <v-spacer />
                <a class="btn mr-5" @click="showAddGroup = false">Cancel</a>
                <a class="btn primary" @click="createGroup">Create Group</a>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { VDataTable } from 'vuetify/components/VDataTable';
import { Class, Group } from '@ilteducation/giglets-types';
import { GigletsBookV1 } from '@ilteducation/content-registry-types';

definePage({ meta: { area: 'pupils-and-groups' } });
const confirm = useConfirmDialog();

const store = useTeacherStore();
const contentSearchStore = useContentSearchStore();
const { formatDate } = useDateFormat();
const { getImageUrl } = useImageCacheReset();

store.fetchPupils();
store.fetchGroups();
const { pupils, groups, isLoaded } = storeToRefs(store);
const assignedBooksStore = useAssignedBooks();
assignedBooksStore.fetchAssignedBooks('teacher', []);
const { assignedBooks } = storeToRefs(assignedBooksStore);

const filterGroup = ref<string>();
const editingNameForGroup = ref<string>();
const showAddGroup = ref(false);
const showEditGroups = ref(false);
const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const newGroupName = ref('');

type SelectablePupil = {
    id: number;
    uid: string;
    name: string;
    classes: Array<{ uid: string; name: string }>;
    groups: Array<{ uid: string; name: string }>;
    noAccess?: boolean;
};

const items = computed(() => {
    const res: SelectablePupil[] = [];
    if (!isLoaded.value.pupils) {
        return res;
    }
    pupils.value.forEach((p) => {
        res.push({
            id: p.id,
            uid: p.uid,
            name: p.name,
            classes: p.classes,
            groups: p.groups,
        });
    });
    // There could be pupils in the teacher's groups that are not in the teacher's classes
    // We add them here, but mark them as noAccess so we don't link to them
    groups.value.forEach((g) => {
        g.pupils.forEach((p) => {
            const existing = res.find((ep) => ep.id === p.id);
            if (existing) {
                if (!existing.groups.map((g) => g.uid).includes(g.uid)) {
                    existing.groups.push(g);
                }
            } else {
                const pupil = pupils.value.find((pv) => pv.id === p.id);
                res.push({
                    id: p.id,
                    uid: p.uid,
                    name: pupil?.name || '',
                    classes: [],
                    groups: [g],
                    noAccess: true,
                });
            }
        });
    });
    return res.filter((p) => {
        if (filterGroup.value) {
            return p.groups.map((g) => g.uid).includes(filterGroup.value);
        } else {
            return true;
        }
    });
});

const pupilsNotInClasses = computed(() => {
    return items.value.filter((p) => p.noAccess);
});

async function updateGroups(pupil: SelectablePupil, newGroups: string[]) {
    // To find the group the pupil has been added to, search through the teacher's groups:
    // * look only at the groups in newGroups
    // * find the group where the pupil isn't a member
    //
    // To find the group the pupil has been removed from, search through the teacher's groups:
    // * look only at the groups NOT in newGroups
    // * find the group where the pupil is a member
    const addedToGroup = groups.value
        .filter((g) => newGroups.includes(g.uid))
        .find((g) => !g.pupils.map((p) => p.id).includes(pupil.id));
    const removedFromGroup = groups.value
        .filter((g) => !newGroups.includes(g.uid))
        .find((g) => g.pupils.map((p) => p.id).includes(pupil.id));

    if (addedToGroup) {
        await store.updateGroup(addedToGroup.uid, { addPupils: [pupil.uid] });
    }
    if (removedFromGroup) {
        await store.updateGroup(removedFromGroup.uid, { removePupils: [pupil.uid] });
    }
}

async function saveGroupName(group: Group) {
    await store.updateGroup(group.uid, { name: newGroupName.value });
    editingNameForGroup.value = undefined;
}

async function createGroup() {
    await store.createGroup(newGroupName.value);
    showAddGroup.value = false;
}

async function deleteGroup(group: Group) {
    const res = await confirm('Delete Group', `Are you sure you want to delete the group '${group.name}'?`);
    if (!res) {
        return;
    }
    await store.deleteGroup(group.uid);
}

async function cleanUpGroups() {
    const toRemove: Record<string, string[]> = {};
    for (const pupil of pupilsNotInClasses.value) {
        for (const group of pupil.groups) {
            if (!toRemove[group.uid]) {
                toRemove[group.uid] = [];
            }
            toRemove[group.uid].push(pupil.uid);
        }
    }
    for (const [groupUid, pupilUids] of Object.entries(toRemove)) {
        await store.updateGroup(groupUid, { removePupils: pupilUids });
    }
}

async function getBooksAssignedToSelectedGroup() {
    sharedBooksInGroup.value = [];
    const pupilIdsInSelectedGroup =
        groups.value.find((group) => group.uid === filterGroup.value)?.pupils.map((pup) => pup.id) || [];
    if (!pupilIdsInSelectedGroup.length) {
        return;
    }
    const assignedBooksInGroup = assignedBooks.value.filter((abo) =>
        pupilIdsInSelectedGroup?.every((id) => abo.assigned_to.some((a) => a.id === id)),
    );
    const bookIds = assignedBooksInGroup.map((book) => book.book_id.toString());
    const books = await contentSearchStore.getBooks(bookIds);
    sharedBooksInGroup.value = books
        .map((book) => {
            const assignedBook = assignedBooksInGroup.find((ab) => ab.book_id.toString() === book.bookId);
            return {
                ...book,
                assigned: assignedBook
                    ? new Date(
                          Math.min(
                              // if book is not assigned to whole group at the same time, display the oldest assignment time
                              ...assignedBook.assigned_to
                                  .filter((at) => pupilIdsInSelectedGroup.includes(at.id) && at.assigned)
                                  .map((fat) => new Date(fat.assigned!).getTime()),
                          ),
                      ).toISOString()
                    : '',
            };
        })
        .sort((b, a) => {
            return b.assigned > a.assigned ? -1 : 1;
        });
}

watch(filterGroup, getBooksAssignedToSelectedGroup);

const sharedBooksInGroup = ref<(GigletsBookV1 & { assigned?: string })[]>([]);

const colorNames = ['red', 'pink', 'purple', 'indigo', 'blue', 'cyan', 'teal', 'green', 'orange', 'brown'];

const headers = [
    {
        title: 'Name',
        key: 'name',
        sortable: true,
    },
    {
        title: 'Classes',
        key: 'classes',
        sortable: false,
    },
    {
        title: 'Groups',
        key: 'groups',
        sortable: false,
    },
];

const hash = (str: string) => {
    let hash = 0;
    for (const char of str) {
        hash = (hash << 5) - hash + char.charCodeAt(0);
        hash |= 0; // Constrain to 32bit integer
    }
    return hash;
};
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
</style>
